// The web app's string catalogue: every word the hub supplies itself — the frame, the navigation,
// the account menu, the page headings and the placeholder lines. It follows the rule of
// @waypoint/surface's catalogue (packages/surface/src/i18n/en.ts): words on screen come from here
// through t(), never from a literal in a component, and apps/web/eslint.config.js enforces it.
//
// Wording follows mockups/CONTRACT.md §3: sentence case, plain words, no exclamation marks. Nothing
// here says how Ascendis works; procedures and titles arrive from the content pack through the API.
//
// `{name}` marks a parameter; t() fills it and throws when it is missing.

export const en = {
  // ── The frame ─────────────────────────────────────────────────────────
  "app.productName": "Waypoint",
  "app.skipToContent": "Skip to content",
  "nav.label": "Main",
  "nav.learn": "Learn",
  "nav.library": "Library",
  "nav.practice": "Practice",
  "nav.record": "My record",
  "nav.admin": "Admin",
  "nav.plan": "Production plan",

  // ── Account menu: the organisation and the role, never a name ─────────
  "account.menu": "Account: {org}, {role}",
  "account.menuNoOrg": "Account: {role}",
  "account.organisation": "Organisation",
  "account.roles": "Role",
  "account.signOut": "Sign out",
  "role.associate": "Associate",
  "role.manager": "Manager",
  "role.content-owner": "Content owner",
  "role.client-reviewer": "Client reviewer",
  "role.tenant-admin": "Tenant administrator",
  "role.platform-admin": "Platform administrator",

  // ── Pages. Each is a heading and an empty state until its task builds it ──
  "page.learn.title": "Learn",
  "page.learn.empty": "Your curriculum appears here",
  "page.learn.emptyLine":
    "Titles by learning path and tier, in the order your library teaches them.",
  "page.title.title": "Title",
  "page.title.empty": "This title opens here",
  "page.title.emptyLine": "Its video, beats and your progress will show on this page.",
  "page.library.title": "Library",
  "page.library.empty": "The video library appears here",
  "page.library.emptyLine": "Every recorded video in teaching order, with what to watch for.",
  "page.practice.title": "Practice",
  "page.practice.empty": "Practice scenarios appear here",
  "page.practice.emptyLine": "A fresh copy with invented cases. Nothing in it is live.",
  "page.scenario.title": "Practice scenario",
  "page.scenario.empty": "This scenario opens here",
  "page.scenario.emptyLine": "The practice copy and the assistant will sit side by side.",
  "page.record.title": "My record",
  "page.record.empty": "Nothing recorded yet.",
  "page.record.emptyLine":
    "Your walkthroughs, open loops and completed titles will be listed here.",
  "page.walkthrough.title": "Walkthrough record",
  "page.walkthrough.empty": "This record opens here",
  "page.walkthrough.emptyLine": "What happened, when, and how the assistant knew.",
  "page.companion.title": "Companion",
  "page.companion.empty": "The Companion opens here",
  "page.companion.emptyLine": "The assistant in its own window, attached to your session.",
  "page.admin.title": "Admin",
  "page.admin.empty": "Administration appears here",
  "page.admin.emptyLine":
    "Policy, model bindings, content packs, audit, usage and egress for your tenant.",
  "page.admin.policy": "Policy",
  "page.admin.bindings": "Model bindings",
  "page.admin.packs": "Content packs",
  "page.admin.audit": "Audit log",
  "page.admin.usage": "Usage",
  "page.admin.egress": "Egress",
  "page.admin.sectionLine": "This section opens here.",
  "page.notFound.title": "Page not found",
  "page.notFound.line": "There is no page at this address.",
  "page.notFound.home": "Go to Learn",
  "page.forbidden.title": "Not available to you",
  "page.forbidden.line": "This page is for tenant administrators.",
  "page.forbidden.issues": "This page is for the client reviewer and the guide's owners.",

  // ── The production plan (E32-S9-T3; mockups/production-picker.html) ───
  "plan.eyebrow": "Which segments we film",
  "plan.title": "Choose the training videos for {org}",
  "plan.who": "{role} · {org}",
  "plan.titleNoOrg": "Choose the training videos",
  "plan.whoNoOrg": "{role}",
  "plan.intro":
    "Pick any segments from the digested guides, up to {budget} of video in all. A segment counts its estimated length until it is rendered, then its measured length. Segments tied to an open question can still be picked. They wait to render until it is answered.",
  "plan.readOnly": "You can read this plan. Changing it is for content owners.",
  "plan.skip": "Skip to the segments",
  "plan.loading": "Loading the plan",
  "plan.failed.title": "The plan can't be loaded",
  "plan.failed.line": "Check your connection, then reload the page.",
  "plan.forbidden.line":
    "This page is for content owners, client reviewers and tenant administrators.",
  "plan.budget": "Production budget",
  "plan.used": "{used}",
  "plan.usedOf": "used of {budget}",
  "plan.remaining": "{time} remaining",
  "plan.overBy": "Over by {time}",
  "plan.meter": "Video time used",
  "plan.meterUnder": "{used} used of {budget}, {left} remaining",
  "plan.meterOver": "{used} used of {budget}, over by {over}",
  "plan.save": "Save plan",
  "plan.saving": "Saving",
  "plan.discard": "Discard changes",
  "plan.pickedBy": "Picked by {who} on {when}",
  "plan.pickedNone": "No plan has been saved yet.",
  "plan.draft": "Unsaved: {added} to add, {removed} to remove",
  "plan.saved": "Plan saved.",
  "plan.conflict": "Someone else changed this plan — reloading",
  "plan.refused.over": "Not saved: the plan would use {used}, past {budget}.",
  "plan.refused.overStored":
    "Not saved: the plan would use {used}, up from {previous}, and it is already past {budget}. A change can only keep it level or bring it down.",
  "plan.refused.other":
    "Not saved: the plan has a segment that can't be picked. Reload the page and try again.",
  "plan.refused.failed": "The plan was not saved. Check your connection and try again.",
  "plan.refused.forbidden": "You can read this plan, not change it.",
  "plan.refuse.room":
    "Not added: {id} is {seconds} and only {left} is left. It would make {total}, past {budget}. Remove something first.",
  "plan.refuse.over":
    "Not added: {id} would make {total}, and the plan is already over {budget} by {over}. Remove segments first, then add one that is no longer than what you removed.",
  "plan.over.title": "This plan is over budget by {over}, now that rendered segments are measured.",
  "plan.over.hold":
    "Segments that have not rendered yet cannot render until the plan is back under {budget}.",
  "plan.over.kept": "Segments already rendered are not affected.",
  "plan.over.allowed":
    "You can remove segments. You cannot add to the plan until it is under {budget}, except to swap one for a shorter one.",
  "plan.blocked.one": "1 picked segment is blocked from render: {ids}.",
  "plan.blocked.many": "{count} picked segments are blocked from render: {ids}.",
  "plan.blocked.line":
    "They count towards the {budget} now. They render once the open question is answered and the script is re-reviewed.",
  "plan.legend.est": "estimated from the script, before it renders",
  "plan.legend.measured": "taken from the rendered video",
  "plan.legend.queue": "Open the source issues queue",
  "plan.guide.picked": "{picked} of {total} picked",
  "plan.guide.pickedTime": "{picked} of {total} picked, {time}",
  "plan.guide.reference": "{guide}, reference entries",
  "plan.guide.referenceOnly": "Reference only",
  "plan.guide.referenceNote":
    "These entries are a reference for checking the others. They are never filmed, so they have no runtime to spend and cannot be picked.",
  "plan.guide.notProduced": "Not produced",
  "plan.seg.est": "est.",
  "plan.seg.measuredWord": "measured",
  "plan.over.region": "Over budget",
  "plan.blocked.region": "Blocked segments",
  "plan.seg.measured": "measured, est. {planned}",
  "plan.seg.blocked": "Blocked from render",
  "plan.seg.questionOne": "Open question: {ids}",
  "plan.seg.questionMany": "Open questions: {ids}",
  "plan.seg.held": "Render held: plan is over {budget}",
  "plan.seg.wontFit": "Won't fit: needs {needs}, {left} left",
  "plan.seg.queueLink": "{id} in the source issues queue",

  // ── Session states ────────────────────────────────────────────────────
  "session.checking": "Checking who is signed in",
  "session.signedOut.title": "You are signed out",
  "session.signedOut.line": "Sign in again to carry on where you were.",
  "session.ended.title": "You have signed out",
  "session.ended.line": "Your session has ended on this device.",
  "session.signIn": "Sign in",
  "session.signIn.line": "Your organisation's sign-in page opens, then brings you back here.",
  "session.failed.title": "Waypoint can't be reached",
  "session.failed.line": "Check your connection, then reload the page.",
  "session.invalid.title": "Waypoint sent an answer this page can't read",
  "session.invalid.line": "Reload the page. If it happens again, tell your tenant administrator.",

  // ── Source issues: the client reviewer's queue (mockups/issues-queue.html, the approved copy) ──
  "nav.issues": "Source issues",
  "issues.skip": "Skip to the queue",
  "issues.eyebrow": "Operations Console guides",
  "issues.title": "Questions for Broadridge about the guides",
  "issues.intro":
    "Where the guides disagree with themselves, say nothing, or look like a typo, we ask rather than guess. Your answer decides what the training says. A video that depends on an open contradiction does not render until you answer or waive it.",
  "issues.introReadOnly":
    "Where the guides disagree with themselves, say nothing, or look like a typo, we ask the guide's owners rather than guess. You can read the queue here. A video that depends on an open contradiction does not render until it is answered or waived.",
  "issues.loading": "Loading the questions",
  "issues.failed.title": "The questions could not be loaded",
  "issues.failed.line": "Check your connection, then try again.",
  "issues.failed.retry": "Try again",
  "issues.forbidden": "Your role cannot read this queue.",
  "issues.empty.title": "There are no questions yet",
  "issues.empty.line": "When the guides raise one, it will appear here.",
  "issues.summary": "Queue summary. Choose a count to filter the list.",
  "issues.tile.open": "Open",
  "issues.tile.openNote": "Waiting for an answer",
  "issues.tile.blocking": "Blocking",
  "issues.tile.blockingNote": "Holding a render",
  "issues.tile.answered": "Answered",
  "issues.tile.answeredNote": "Back to review before render",
  "issues.total": "{count} issues in all",
  "issues.totalWaived": "{count} issues in all, {waived} waived",
  "issues.list": "Issues",
  "issues.listLabel": "Source issues",
  "issues.filters": "Filter issues",
  "issues.filter.kind": "Kind",
  "issues.filter.origin": "Origin",
  "issues.filter.guide": "Guide",
  "issues.filter.status": "Status",
  "issues.filter.allKinds": "All kinds",
  "issues.filter.allOrigins": "All origins",
  "issues.filter.allGuides": "All guides",
  "issues.filter.allStatuses": "All statuses",
  "issues.filter.blocking": "Blocking (holding a render)",
  "issues.filter.clear": "Clear filters",
  "issues.showing": "Showing {shown} of {total}",
  "issues.noMatch": "Nothing matches these filters.",
  "issues.kind.contradiction": "Contradiction",
  "issues.kind.silence": "Silence",
  "issues.kind.probable-typo": "Probable typo",
  "issues.kind.pipeline-finding": "Pipeline finding",
  "issues.origin.breakdown": "From the breakdown",
  "issues.origin.pipeline": "Found by the pipeline",
  "issues.status.open": "Open",
  "issues.status.answered": "Answered",
  "issues.status.waived": "Waived",
  "issues.status.reopened": "Reopened, open",
  "issues.blocksRender": "Blocks render",
  "issues.guide.dashboard": "Operations Console Dashboard",
  "issues.guide.acat": "Account Transfer (Ops Console edition)",
  "issues.guide.caip": "Corporate Actions and Income Processing",
  "issues.guide.acat-bps-us": "ACAT, BPS U.S. edition (reference)",
  "issues.pack.label": "Questions about",
  "issues.pack.ops-console-dashboard": "Dashboard",
  "issues.pack.ops-console-acat": "Account Transfer (ACAT)",
  "issues.pack.ops-console-caip": "CAIP",
  "issues.meta.guide": "Guide",
  "issues.meta.printed": "Printed",
  "issues.meta.segments": "Segments",
  "issues.pages.one": "Page {pages}",
  "issues.pages.many": "Pages {pages}",
  "issues.pages.none": "No page cited",
  "issues.pages.provisional": "{pages} (provisional)",
  "issues.noSegment": "No segment narrates it",
  "issues.row.open": "{id}, open the answer panel",

  // The answer panel
  "issues.panel.empty.title": "Answer an issue",
  "issues.panel.empty.line1":
    "Choose an issue from the list. You can answer three ways: pick which cited page is right, write your own answer, or confirm a value we inferred.",
  "issues.panel.empty.line2":
    "If you cannot rule on it, waive it with a reason. A waived issue stops holding anything up.",
  "issues.panel.emptyReadOnly":
    "Choose an issue from the list to read it. Answers come from the client reviewer.",
  "issues.panel.back": "Back to the list",
  "issues.panel.question": "The question",
  "issues.panel.printed": "Printed pages",
  "issues.panel.provisional": "Provisional",
  "issues.panel.segments": "Segments that narrate it",
  "issues.panel.nextOpen": "Next open issue",
  "issues.panel.noMoreOpen": "No more open issues in this list.",
  "issues.gate.holds": "This holds up {segments}.",
  "issues.gate.holdsLine": "Renders of those segments are refused until you answer or waive it.",
  "issues.gate.holdsReadOnly":
    "Renders of those segments are refused until it is answered or waived.",
  "issues.gate.noneHeld": "Open contradiction, but nothing is held up.",
  "issues.gate.noneHeldLine":
    "No segment narrates it, so no render waits on it. It still needs an answer before the training goes live.",
  "issues.gate.open": "Does not hold up a render.",
  "issues.gate.openLine":
    "It stays here until it is answered or waived, and it needs one or the other before the training goes live.",
  "issues.followUp.title": "A question from our team",
  "issues.followUp.by": "{by}, {at}. This reopened the issue.",

  // The three forms
  "issues.form.legend": "How do you want to answer?",
  "issues.form.reading": "Choose a reading",
  "issues.form.readingHint": "One of the pages we cite is the right one.",
  "issues.form.readingNone": "This issue cites no page, so there is nothing to choose between.",
  "issues.form.readingWhich": "Which page is correct?",
  "issues.form.text": "Write an answer",
  "issues.form.textHint": "In your own words. Our team reviews it before it becomes narration.",
  "issues.form.textLabel": "Your answer",
  "issues.form.confirm": "Confirm the value: {pages}",
  "issues.form.confirmGeneric": "Confirm an inferred value",
  "issues.form.confirmHint": "The pipeline numbered these pages. Tell us it is right.",
  "issues.form.confirmNone": "Nothing was inferred on this issue.",
  "issues.form.inferred": "Our inferred value",
  "issues.form.inferredValue": "Printed pages {pages}",
  "issues.form.inferredNote":
    "The guide shows no printed number on these pages, so the pipeline numbered them from the pages around them.",
  "issues.form.confirmBox": "These pages are numbered correctly.",
  "issues.form.save": "Save answer",
  "issues.form.saving": "Saving",
  "issues.form.recordedAs": "Recorded in your name, with the time.",

  // Waiving
  "issues.waive.open": "Waive this issue",
  "issues.waive.label": "Why are you waiving it? (required)",
  "issues.waive.go": "Waive with this reason",
  "issues.waive.cancel": "Cancel",

  // Answered, waived, reopened
  "issues.done.answered": "Answered",
  "issues.done.by": "By {by} on {at}.",
  "issues.done.backToReview":
    "{segments} go back to review before they can render, so the script is re-checked against your answer.",
  "issues.done.changeNote": "An answer can be changed only if our team asks a follow-up.",
  "issues.done.waived": "Waived",
  "issues.done.waivedLine": "Nothing waits on it any more.",
  "issues.answerText.reading": "The right reading: {page}.",
  "issues.answerText.confirm": "Confirmed: {value}",

  // Read-only roles
  "issues.readOnly.title": "Read only",
  "issues.readOnly.line":
    "Only the client reviewer answers or waives. You can read this issue and its history.",
  "issues.reopen.label": "Your follow-up question",
  "issues.reopen.hint": "Asking reopens the issue. It does not record an answer.",
  "issues.reopen.go": "Ask a follow-up",

  // Errors and messages
  "issues.error.required.text": "Write your answer first.",
  "issues.error.required.reading": "Choose which page is correct, or answer another way.",
  "issues.error.required.confirm": "Tick the box to confirm the value.",
  "issues.error.required.reason": "A waiver needs a reason. Say why this does not need an answer.",
  "issues.error.required.question": "Write the question you want to ask.",
  "issues.error.field": "That answer was refused. Check this field and try again.",
  "issues.error.page": "That page is not one this issue cites. Choose one of the pages listed.",
  "issues.error.conflict": "Someone already answered this — reloading",
  "issues.error.forbidden": "Your role cannot do this. The queue is read only for you.",
  "issues.error.failed": "That did not save. Check your connection and try again.",
  "issues.announce.answered": "{id} answered. {counts}",
  "issues.announce.waived": "{id} waived. {counts}",
  "issues.announce.reopened": "{id} reopened with your question. {counts}",
  "issues.announce.counts": "Open {open}, blocking {blocking}, answered {answered}.",
  "issues.announce.showing": "Showing {count} issues.",

  // ── Scripts: the owner's script review (E31-S6-T1, E31-S6-T3; mockups/script-review.html) ──
  "nav.scripts": "Scripts",
  "scripts.eyebrow": "{pack} · training videos",
  "scripts.eyebrowPlain": "Training videos",
  "scripts.title": "Scripts for review",
  "scripts.intro":
    "Read each script as the video will say it: the narration scene by scene, the guide page each scene comes from, the screen it shows, and the questions at the end. Approving a script releases its video. When the video is ready, watch it and publish it; learners see nothing before you do. Send either back with a note if anything should change.",
  "scripts.list": "Videos in the plan",
  "scripts.budget":
    "{waiting} waiting for your review · {count} videos, about {used} of the {budget} budget",
  "scripts.loading": "Loading the scripts",
  "scripts.failed.title": "The scripts can't be loaded",
  "scripts.failed.line": "Check your connection, then reload the page.",
  "scripts.forbidden": "Scripts are for content owners.",
  "scripts.empty.title": "No videos are in the plan yet",
  "scripts.empty.line":
    "When videos are picked in the production plan, their scripts are listed here.",
  "scripts.noDraft":
    "No draft yet. The drafting agent adds it here when it is ready, and it appears in this list for your review.",
  "scripts.rowFailed": "Not loaded",
  "scripts.versions": "All versions",

  "stage.none": "No draft yet",
  "stage.draft": "Draft",
  "stage.sent-back": "Sent back",
  "stage.waiting": "Waiting for you",
  "stage.waitingOther": "Waiting for review",
  "stage.held": "Held: waiting on Broadridge",
  "stage.rendering": "Approved · video rendering",
  "stage.ready": "Video ready to watch",
  "stage.readyTime": "Video ready to watch, {time}",
  "stage.failed": "Video failed",
  "stage.live": "Live",
  "stage.liveTime": "Live, {time}",
  "stage.retired": "Retired",
  "stage.cancelled": "Cancelled",

  "review.version": "Version {n}",
  "review.versionFirst": "Version {n} · first version",
  "review.facts.guide": "Guide",
  "review.facts.guideValue": "{guide}, {edition}",
  "review.facts.runtime": "Runtime",
  "review.facts.measured": "{time} measured",
  "review.facts.estimated": "about {time} estimated",
  "review.facts.scenes": "Scenes",
  "review.facts.questions": "Questions",
  "review.first":
    "This is the first version of this video, so there is nothing to compare it with. Later versions show the changes side by side.",
  "review.parts": "Script parts",
  "review.tab.script": "Script and stills",
  "review.tab.questions": "Knowledge check",
  "review.scene": "Scene {n}",
  "review.scene.words": "{words} words · about {seconds} s",
  "review.scene.title": "Title card",
  "review.scene.avatar": "The presenter",
  "review.scene.still": "On screen",
  "review.scene.video": "Screen recording",
  "review.cite": "Guide {pages}",
  "review.still.alt": "Still for scene {n}",
  "review.still.altNamed": "Still for scene {n}: {name}",
  "review.still.none": "No still: {what}",
  "review.qs.note": "Asked after the video. The right answer is marked.",
  "review.qs.segment": "Segment {n} · {label}",
  "review.qs.q": "Q{n}",
  "review.qs.right": "Right answer",
  "review.qs.why": "Why: {why}",
  "review.qs.unread": "This question set can't be laid out here, so it is shown as written.",
  "review.loading": "Loading the script",
  "review.failed.title": "This script can't be loaded",
  "review.failed.line": "Check your connection, then reload the page.",
  "review.notFound": "There is no version at this address.",

  "review.decide.yours": "Your decision",
  "review.decide.waiting": "Waiting for review",
  "review.decide.cantApprove": "You can't approve this script.",
  "review.decide.cantApproveLine": "You drafted and submitted it. Someone else has to approve it.",
  "review.decide.held": "Held: waiting on Broadridge.",
  "review.decide.heldBefore": "This script depends on open questions:",
  "review.decide.heldAfter":
    "It can't be approved until they are answered, because an answer may change what it says.",
  "review.decide.heldLink": "{id} in the source issues queue",
  "review.decide.filming":
    "Approving sends this script to be filmed: about {time} of video with the presenter. Nothing renders until you approve.",
  "review.decide.tick":
    "I have read every scene and question, and checked them against the cited pages.",
  "review.decide.teaches": "Did what this title teaches change?",
  "review.decide.teachesYes": "Yes. Learners who completed it complete it again.",
  "review.decide.teachesNo": "No. Completions stay as they are.",
  "review.approve": "Approve script",
  "review.approving": "Approving",
  "review.sendBack": "Send back",
  "review.note.label": "What should change? The drafting agent sees this note.",
  "review.note.placeholder": "For example: scene 2 should say…",
  "review.note.go": "Send back with this note",
  "review.note.cancel": "Cancel",
  "review.note.required": "Write what should change first.",
  "review.draft.title": "Draft",
  "review.draft.sentBack": "Sent back",
  "review.draft.yourNote": "Your note",
  "review.draft.theNote": "The approver's note",
  "review.draft.revise": "The drafting agent revises it and submits it again.",
  "review.draft.notSubmitted": "Not yet submitted for review.",
  "review.ready.drafterTitle": "Video ready",
  "review.ready.drafterLine": "Waiting for the approver to watch and publish.",
  "review.ready.title": "Watch, then publish",
  "review.ready.player": "The rendered video, {time}",
  "review.ready.line":
    "Rendered from the script you approved. Learners see it only when you publish it. The intro card plays first, as it will for learners, and shows this version as not yet published. If the video is wrong, send it back with a note.",
  // The video intro card the player draws before the video (ADR-036).
  "review.intro.region": "Video introduction",
  "review.intro.guide": "Guide",
  "review.intro.section": "Section",
  "review.intro.topic": "Topic",
  "review.intro.skip": "Skip intro",
  "review.intro.published": "Version {number} · Published {date}",
  "review.intro.unpublished": "Version {number} · Not yet published",
  "review.intro.edition": "Guide edition {edition}",
  "review.publish": "Publish to learners",
  "review.publishing": "Publishing",
  "review.rendering.title": "Approved · video rendering",
  "review.rendering.line": "Usually ready within half an hour. You don't need to do anything else.",
  "review.videoFailed.title": "Video failed",
  "review.videoFailed.line":
    "The video did not render. The script stays approved. Send it back with a note if it should change.",
  "review.videoFailed.reason": "Why: {reason}",
  "review.renderAgain": "Request render again",
  "review.renderAgain.sending": "Requesting",
  "review.live.title": "Live",
  "review.live.line": "Learners see this version.",
  "review.retired.title": "Retired",
  "review.retired.line": "A later version replaced this one.",
  "review.cancelled.title": "Cancelled",
  "review.cancelled.line": "This version was withdrawn.",
  "review.history": "History",
  "review.trail.created": "Drafted by {who}",
  "review.trail.edited": "Edited by {who}",
  "review.trail.submitted": "Submitted for review by {who}",
  "review.trail.sentBack": "Sent back by {who}: {note}",
  "review.trail.approved": "Approved by {who}; video requested",
  "review.trail.published": "Published by {who}, after watching it",
  "review.trail.retired": "Retired when a later version was published",
  "review.trail.cancelled": "Cancelled by {who}",
  "review.you": "you",
  "review.colleague": "a colleague",

  // The API's refusals, in the catalogue's words
  "review.refused.approve":
    "You can't approve this script: you drafted, submitted or edited it. Someone else has to approve it.",
  "review.refused.publish":
    "You can't publish this video: you drafted, submitted or edited its script. Someone else has to publish it.",
  "review.refused.sendBack": "Your role can't send this script back.",
  "review.refused.conflict": "This script changed since you opened it — reloading",
  "review.refused.issues":
    "Not published: a source issue linked to this title is open. Publish it once the issue is answered or waived.",
  "review.refused.render": "Not published: the video has not finished rendering.",
  "review.refused.renderHash":
    "Not published: the stored video is not the one that was rendered. Send it back to render it again.",
  "review.refused.contradiction":
    "Not approved: an open question on this title may change the script. Approve it once the question is answered.",
  "review.refused.overBudget":
    "Not approved: the production plan is over its budget, so a new video can't render.",
  "review.refused.renderOther": "Not approved: the video could not be requested.",
  "review.refused.renderAgain": "Your role can't ask for this video again.",
  "review.refused.renderNotApproved":
    "Not requested: this script is no longer approved, so there is nothing to render.",
  "review.refused.contradictionAgain":
    "Not requested: an open question on this title may change the script. Ask again once the question is answered.",
  "review.refused.overBudgetAgain":
    "Not requested: the production plan is over its budget, so a new video can't render.",
  "review.refused.titleUnknown":
    "Not requested: the content pack no longer has this title. Ask a platform operator.",
  "review.refused.themeUnknown":
    "Not requested: the look this script names is not set up. Ask a platform operator.",
  "review.refused.note": "The note was refused. Write what should change, then send it again.",
  "review.refused.failed": "That did not go through. Check your connection and try again.",
  "review.announce.approved": "{id} approved. Its video is rendering.",
  "review.announce.sentBack": "{id} sent back with your note.",
  "review.announce.published": "{id} is live.",

  "renderFailure.provider-failed": "the video service could not make it",
  "renderFailure.provider-timeout": "the video service took too long",
  "renderFailure.still-missing": "a still was missing",
  "renderFailure.invalid-script": "the video service refused the script",
  "renderFailure.cancelled": "the render was cancelled",
  "renderFailure.quota": "the video service's quota was used up",
  "renderFailure.invalid-request": "the request could not be built from the script",
  "renderFailure.still-mismatch": "a still is not the one the script declares",
  "renderFailure.submit-failed": "the video service did not take the job",
  "renderFailure.poll-budget": "the video did not finish in time",
  "renderFailure.hash-mismatch": "a file from the video service was not the one it declared",
  "renderFailure.asset-unavailable": "the video service no longer holds the files",
  "renderFailure.invalid-result": "the video service's result could not be read",
  "renderFailure.copy-failed": "the video could not be stored",
  "renderFailure.version-not-approved": "the script left approval while it rendered",
  "renderFailure.other": "{code}",

  // The versions list (E31-S6-T1)
  "versions.eyebrow": "Versions of {id}",
  "versions.list": "Versions, newest first",
  "versions.number": "Version {n}",
  "versions.pack": "Pack edition",
  "versions.packLine": "As the content pack ships it, edition {version}",
  "versions.contentVersion": "Content version {cv}",
  "versions.served": "Served to learners",
  "versions.actor": "{act} · {when}",
  "versions.rendering": "Video rendering",
  "versions.failed": "Video failed",
  "versions.failedReason": "Video failed: {reason}",
  "versions.review": "Review",
  "versions.reviewLabel": "Review version {n}",
  "versions.open": "Open",
  "versions.openLabel": "Open version {n}",
  "versions.loading": "Loading the versions",
  "versions.failed.title": "The versions can't be loaded",
  "versions.notFound": "The active pack has no title {id}.",
  "versions.back": "Back to the scripts",
  "versions.state.draft": "Draft",
  "versions.state.in-review": "In review",
  "versions.state.approved": "Approved",
  "versions.state.published": "Published",
  "versions.state.retired": "Retired",
  "versions.state.cancelled": "Cancelled",
  "versions.edit": "Edit",
  "versions.editLabel": "Edit version {n}",

  // The draft editor (E31-S6-T2; mockups/content-edit.html). Every word here is about editing; the
  // scene text, questions and notes are the drafter's and the approver's own, from the API.
  "edit.eyebrow": "Edit a draft",
  "edit.open": "Edit this draft",
  "edit.loading": "Loading the draft",
  "edit.loadingStills": "Loading the stills",
  "edit.failed.title": "This draft can't be loaded",
  "edit.stillsFailed":
    "A still of this draft can't be loaded, so the draft can't be saved. Reload the page to try again.",
  "edit.notDraft.title": "This version is no longer a draft",
  "edit.notDraft.line": "Only a draft can be edited. Open it to see where it stands.",
  "edit.notDraft.open": "Open the version",
  "edit.state.sentBack": "Draft (sent back)",
  "edit.state.draft": "Draft",
  "edit.state.inReview": "In review",
  "edit.version": "Version {n}",
  "edit.facts.guidePages": "Guide pages",
  "edit.facts.scenes": "Scenes",
  "edit.facts.runtime": "Runtime",
  "edit.facts.runtimeValue": "about {time} estimated",
  "edit.sentBack": "Sent back by {who}, {when}:",
  "edit.sentBackLabel": "Why this draft came back",
  "edit.side": "Version details",

  "edit.bar.label": "Save and submit",
  "edit.bar.problem": "1 problem stops the save",
  "edit.bar.problems": "{n} problems stop the save",
  "edit.bar.fixOne": "Fix it to save:",
  "edit.bar.fix": "Fix them to save:",
  "edit.bar.problemsLabel": "Problems to fix",
  "edit.bar.unsaved": "Unsaved changes",
  "edit.bar.saveFirst": "Saves your changes first, then submits.",
  "edit.bar.saved": "Saved",
  "edit.bar.noChanges": "No changes to save.",
  "edit.bar.checking": "Checking the question set.",
  "edit.bar.notSaved": "Not saved",
  "edit.bar.gone": "This version is no longer a draft, so it can't be saved.",
  "edit.bar.submitted": "Submitted for review",
  "edit.bar.inReview": "In review: it can't be edited now.",
  "edit.cancel": "Cancel",
  "edit.back": "Back to the script",
  "edit.save": "Save draft",
  "edit.saving": "Saving",
  "edit.submit": "Submit for review",
  "edit.saveSubmit": "Save and submit for review",
  "edit.submitting": "Submitting",
  "edit.discard.label": "Discard changes?",
  "edit.discard.ask": "Leave without saving? Your changes are lost.",
  "edit.discard": "Discard changes",
  "edit.keep": "Keep editing",

  "edit.prob.still": "Scene {n}: image",
  "edit.prob.narration": "Scene {n}: narration",
  "edit.prob.pages": "Scene {n}: guide page",
  "edit.prob.box": "Scene {n}: highlight box",
  "edit.prob.questionsLine": "Question set: line {line}",
  "edit.prob.questions": "Question set: {n} problems",
  "edit.prob.questionsServer": "Question set: refused when saved",
  "edit.prob.cv": "Content version",
  "edit.prob.size": "Size of this save",

  "edit.conflict.title": "This version is no longer a draft.",
  "edit.conflict.line":
    "It was submitted for review or moved on since you opened it, so your changes were not saved. They stay on this page until you leave it.",
  "edit.conflict.open": "Open the version as it is now",
  "edit.refused.title": "The draft was not saved.",
  "edit.refused.fields": "Some of it was refused. The fields are marked.",
  "edit.refused.other": "Some of it was refused. Check the draft and try again.",
  "edit.refused.forbidden": "You can't edit this draft.",
  "edit.refused.failed": "That did not go through. Check your connection and try again.",
  "edit.refused.submit": "The draft is saved, but it was not submitted.",
  "edit.submitted.title": "Submitted for review.",
  "edit.submitted.line":
    "It waits for the approver now. It can be edited again only if it is sent back.",
  "edit.announce.saved": "Draft saved.",
  "edit.announce.submitted": "Submitted for review.",
  "edit.announce.box": "Scene {n}: highlight box added in the middle of the still.",
  "edit.announce.noBox": "Scene {n}: no highlight.",
  "edit.announce.still": "Scene {n}: {name} is ready. It is saved with the draft.",

  "edit.scenes.title": "Scenes, in order",
  "edit.scenes.lead":
    "Each scene is said as written. Its guide page must be inside this title's pages, {pages}. Open a scene to edit it.",
  "edit.scenes.leadNoPages":
    "Each scene is said as written, and cites the guide page it comes from. Open a scene to edit it.",
  "edit.scene": "Scene {n}",
  "edit.scene.toFix": "{n} to fix",
  "edit.scene.changed": "Changed",
  "edit.scene.noPage": "no page",
  "edit.still.alt":
    "Still for scene {n}, with the highlight box at left {x}, top {y}, {w} by {h} pixels",
  "edit.still.altNoBox": "Still for scene {n}, with no highlight box",
  "edit.still.info": "{size} · {w} × {h} px",
  "edit.still.new": "New, saved with the draft",
  "edit.still.notUsed": "Not used: {name}",
  "edit.still.replace": "Replace image",
  "edit.still.hint": "PNG or JPEG, up to 8 MB.",
  "edit.still.reading": "Reading the image",
  "edit.still.tooBig": "This image is {size}. An image can be at most 8 MB. Use a smaller image.",
  "edit.still.type": "Use a PNG or JPEG image.",
  "edit.still.bodyOver":
    "This image is too large: the draft would be over the 32 MB limit. Use a smaller image.",
  "edit.still.server":
    "This image was refused when the draft was saved. Choose it again or another.",
  "edit.box.legend": "Highlight box",
  "edit.box.x": "Left (x)",
  "edit.box.y": "Top (y)",
  "edit.box.w": "Width",
  "edit.box.h": "Height",
  "edit.box.hint": "In pixels of the image, which is {w} × {h}. The outline moves as you type.",
  "edit.box.none": "No highlight. The scene shows the whole still.",
  "edit.box.add": "Add a highlight box",
  "edit.box.remove": "No highlight",
  "edit.box.whole": "Each number is a whole number of pixels, and width and height are at least 1.",
  "edit.box.right":
    "The box runs past the right edge: left plus width is {sum}, and the image is {w} wide.",
  "edit.box.bottom":
    "The box runs past the bottom edge: top plus height is {sum}, and the image is {h} high.",
  "edit.box.server": "This box was refused when the draft was saved. Check the numbers.",
  "edit.narration": "Narration",
  "edit.narration.empty": "Write the narration. A scene can't be silent.",
  "edit.narration.long": "The narration is {n} characters. A scene can have at most 4,000.",
  "edit.narration.server": "This narration was refused when the draft was saved. Check it.",
  "edit.pages": "Guide page",
  "edit.pages.guide": "This title's guide pages: {pages}",
  "edit.pages.empty": "Write the guide page.",
  "edit.pages.format": "Write the page as p.7, pp.7–8 or pp.7–8, 9.",
  "edit.pages.outside":
    "{cited} is outside this title's guide pages, {pages}. Cite a page from {pages}.",
  "edit.pages.server":
    "This page was refused when the draft was saved: it must be one of the title's guide pages.",

  "edit.qs.title": "Knowledge check",
  "edit.qs.lead":
    "The question set, as Markdown. It is checked as you type, and the draft can't be saved while a problem stands.",
  "edit.qs.label": "Question set",
  "edit.qs.grammar": "How a question is written",
  "edit.qs.grammarText":
    "### Segment 1 · <label>\n1. <the question>\n- a. <an option>\n- b. **<the right option>** ✓\n- c. <an option>\nWhy: <the reason> (Guide <pages>)",
  "edit.qs.grammarRule":
    "Two or three questions a segment, two to four options each, one right answer, and a guide page inside this title's pages.",
  "edit.qs.check": "Check",
  "edit.qs.problem": "1 problem",
  "edit.qs.problems": "{n} problems",
  "edit.qs.problemLine":
    "The draft can't be saved until this is fixed. Choose it to go to its line.",
  "edit.qs.problemsLine":
    "The draft can't be saved until these are fixed. Choose one to go to its line.",
  "edit.qs.line": "Line {line}",
  "edit.qs.ok": "No problems",
  "edit.qs.okLine": "Each question has one right answer and a guide page.",
  "edit.qs.okQuestion": "{options} options, right answer {right}",
  "edit.qs.server":
    "The question set was refused when the draft was saved. Check it and save again.",

  "edit.cv.title": "Content version",
  "edit.cv.label": "Content version",
  "edit.cv.empty": "Write a content version, for example 2.",
  "edit.cv.how": "If yes, raise it. If no, keep the one served now, {cv}.",
  "edit.cv.howNoServed": "If yes, raise it. If no, keep it.",
  "edit.cv.kept": "Kept at {cv}, the content version served now. Completions stay as they are.",
  "edit.cv.raised":
    "Raised from {from} to {to}. Learners who completed it complete it again. The approver is asked the same question, and a No keeps {from}.",
  "edit.cv.server": "This content version was refused when the draft was saved.",

  "edit.size.title": "Size of this save",
  "edit.size.line":
    "About {size} of the 32 MB limit. Every still goes with the save, and an image grows by about a third on the way.",
  "edit.size.each": "Each image: PNG or JPEG, up to 8 MB.",
  "edit.size.over": "The draft is over the 32 MB limit. Use smaller images.",
  "edit.size.mb": "{n} MB",
} as const satisfies Record<string, string>;

export type MessageKey = keyof typeof en;
